import { useState } from 'preact/hooks';
import { getAuthHeaders } from '../../../utils/auth';

interface Team {
  teamId: string;
  name: string;
  description?: string;
  memberCount: number;
  role: 'admin' | 'member';
  status: 'active' | 'deactivated';
}


interface CreateTeamModalProps {
  onClose: () => void;
  onTeamCreated: (team: Team) => void;
}

export function CreateTeamModal({ onClose, onTeamCreated }: CreateTeamModalProps) {
  const [teamName, setTeamName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          name: teamName.trim(),
          description: description.trim() || undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create team');
      }

      const data = await response.json();
      const newTeam: Team = {
        teamId: data.team.teamId,
        name: data.team.name,
        description: data.team.description,
        memberCount: 1, // Creator is the first member
        role: 'admin',
        status: 'active'
      };

      onTeamCreated(newTeam);
    } catch (err) {
      console.error('Error creating team:', err);
      setError(err instanceof Error ? err.message : 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: Event) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div class="modal modal-open" onClick={handleBackdropClick}>
      <div class="modal-box w-11/12 max-w-md">
        <h3 class="font-bold text-lg mb-4">Create New Team</h3>

        <form onSubmit={handleSubmit}>
          <div class="form-control w-full mb-4">
            <label class="label">
              <span class="label-text">Team Name *</span>
            </label>
            <input
              type="text"
              placeholder="Enter team name"
              class="input input-bordered w-full"
              value={teamName}
              onChange={(e) => setTeamName((e.target as HTMLInputElement).value)}
              maxLength={50}
              required
            />
          </div>

          <div class="form-control w-full mb-6">
            <label class="label">
              <span class="label-text">Description (Optional)</span>
            </label>
            <textarea
              placeholder="What's this team for?"
              class="textarea textarea-bordered h-20"
              value={description}
              onChange={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
              maxLength={200}
            />
            <label class="label">
              <span class="label-text-alt text-base-content/60">
                {description.length}/200 characters
              </span>
            </label>
          </div>

          {error && (
            <div class="alert alert-error mb-4">
              <span class="text-sm">{error}</span>
            </div>
          )}

          <div class="modal-action">
            <button
              type="button"
              class="btn btn-ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={loading || !teamName.trim()}
            >
              {loading ? (
                <>
                  <span class="loading loading-spinner loading-sm"></span>
                  Creating...
                </>
              ) : (
                'Create Team'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
